import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Plus, Trash2, Edit2, CheckCircle2, Clock, Target, AlertTriangle,
  Calendar as CalendarIcon, Loader2
} from 'lucide-react';
import { format } from 'date-fns';

const Decisions = () => {
  const { api } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('decisions');
  const [decisions, setDecisions] = useState([]);
  const [commitments, setCommitments] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('decision');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [decRes, comRes, priRes] = await Promise.all([
        api.get('/api/decisions'),
        api.get('/api/commitments'),
        api.get('/api/priorities')
      ]);
      setDecisions(decRes.data);
      setCommitments(comRes.data);
      setPriorities(priRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api]);

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    if (item) {
      setFormData({ ...item });
    } else {
      setFormData(
        type === 'decision' 
          ? { title: '', description: '', priority: 'medium', deadline: null }
          : type === 'commitment'
          ? { title: '', description: '', deadline: new Date() }
          : { title: '', description: '', rank: priorities.length + 1 }
      );
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setFormData({});
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const endpoint = modalType === 'decision' ? '/api/decisions' 
                     : modalType === 'commitment' ? '/api/commitments' 
                     : '/api/priorities';
      
      if (editingItem) {
        await api.put(`${endpoint}/${editingItem.id}`, formData);
      } else {
        await api.post(endpoint, formData);
      }
      await fetchData();
      closeModal();
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(t('tracker.deleteConfirm'))) return;
    
    try {
      const endpoint = type === 'decision' ? '/api/decisions'
                     : type === 'commitment' ? '/api/commitments'
                     : '/api/priorities';
      await api.delete(`${endpoint}/${id}`);
      await fetchData();
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleStatusChange = async (type, id, newStatus) => {
    try {
      const endpoint = type === 'decision' ? '/api/decisions' : '/api/commitments';
      await api.put(`${endpoint}/${id}`, { status: newStatus });
      await fetchData();
    } catch (error) {
      console.error('Status update error:', error);
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'low': return 'priority-low';
      case 'medium': return 'priority-medium';
      case 'high': return 'priority-high';
      case 'critical': return 'priority-critical';
      default: return '';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'completed': return 'status-completed';
      case 'abandoned': return 'status-abandoned';
      case 'active': return 'status-active';
      case 'missed': return 'status-missed';
      default: return '';
    }
  };

  const getModalTitle = () => {
    const action = editingItem ? t('tracker.edit') : t('tracker.add');
    const typeLabel = modalType === 'decision' ? t('tracker.tabs.decisions') 
                    : modalType === 'commitment' ? t('tracker.tabs.commitments')
                    : t('tracker.tabs.priorities');
    return `${action} ${typeLabel}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 text-atlasCyan animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="decisions-page" className="p-6 lg:p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('tracker.title')}</h1>
          <p className="text-atlasTextSecondary">{t('tracker.subtitle')}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="glass-card mb-6">
          <TabsTrigger value="decisions" data-testid="decisions-tab" className="data-[state=active]:bg-atlasCyan/20 data-[state=active]:text-atlasCyan">
            <Clock className="w-4 h-4 mr-2" />
            {t('tracker.tabs.decisions')} ({decisions.length})
          </TabsTrigger>
          <TabsTrigger value="commitments" data-testid="commitments-tab" className="data-[state=active]:bg-atlasCyan/20 data-[state=active]:text-atlasCyan">
            <Target className="w-4 h-4 mr-2" />
            {t('tracker.tabs.commitments')} ({commitments.length})
          </TabsTrigger>
          <TabsTrigger value="priorities" data-testid="priorities-tab" className="data-[state=active]:bg-atlasCyan/20 data-[state=active]:text-atlasCyan">
            <AlertTriangle className="w-4 h-4 mr-2" />
            {t('tracker.tabs.priorities')} ({priorities.length})
          </TabsTrigger>
        </TabsList>

        {/* Decisions Tab */}
        <TabsContent value="decisions">
          <div className="flex justify-end mb-4">
            <Button 
              data-testid="add-decision-btn"
              onClick={() => openModal('decision')}
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {t('tracker.addDecision')}
            </Button>
          </div>
          <div className="space-y-3">
            {decisions.length === 0 ? (
              <div className="glass-card p-8 text-center">
                <p className="text-atlasTextSecondary">{t('tracker.noDecisions')}</p>
              </div>
            ) : (
              decisions.map((d) => (
                <div key={d.id} data-testid={`decision-item-${d.id}`} className="glass-card p-4 flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-medium text-white">{d.title}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${getPriorityClass(d.priority)}`}>
                        {t(`tracker.priority.${d.priority}`)}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusClass(d.status)}`}>
                        {t(`tracker.status.${d.status}`)}
                      </span>
                    </div>
                    {d.description && <p className="text-atlasTextSecondary text-sm">{d.description}</p>}
                    {d.deadline && (
                      <p className="text-xs text-atlasTextSecondary mt-2 flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" />
                        {t('tracker.due')} {format(new Date(d.deadline), 'MMM d, yyyy')}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {d.status === 'pending' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStatusChange('decision', d.id, 'completed')}
                        className="text-green-500 hover:text-green-400"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => openModal('decision', d)}>
                      <Edit2 className="w-4 h-4 text-atlasTextSecondary" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete('decision', d.id)}>
                      <Trash2 className="w-4 h-4 text-atlasRed" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Commitments Tab */}
        <TabsContent value="commitments">
          <div className="flex justify-end mb-4">
            <Button 
              data-testid="add-commitment-btn"
              onClick={() => openModal('commitment')}
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {t('tracker.addCommitment')}
            </Button>
          </div>
          <div className="space-y-3">
            {commitments.length === 0 ? (
              <div className="glass-card p-8 text-center">
                <p className="text-atlasTextSecondary">{t('tracker.noCommitments')}</p>
              </div>
            ) : (
              commitments.map((c) => (
                <div key={c.id} data-testid={`commitment-item-${c.id}`} className={`glass-card p-4 flex items-center justify-between ${c.is_overdue ? 'border-atlasRed/50' : ''}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className={`text-lg font-medium ${c.is_overdue ? 'text-atlasRed' : 'text-white'}`}>{c.title}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusClass(c.status)}`}>
                        {t(`tracker.status.${c.status}`)}
                      </span>
                      {c.is_overdue && (
                        <span className="text-xs px-2 py-1 rounded-full bg-atlasRed/20 text-atlasRed">{t('tracker.overdue')}</span>
                      )}
                    </div>
                    {c.description && <p className="text-atlasTextSecondary text-sm">{c.description}</p>}
                    <p className="text-xs text-atlasTextSecondary mt-2 flex items-center gap-1">
                      <CalendarIcon className="w-3 h-3" />
                      {t('tracker.deadline')}: {format(new Date(c.deadline), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.status === 'active' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStatusChange('commitment', c.id, 'completed')}
                        className="text-green-500 hover:text-green-400"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => openModal('commitment', c)}>
                      <Edit2 className="w-4 h-4 text-atlasTextSecondary" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete('commitment', c.id)}>
                      <Trash2 className="w-4 h-4 text-atlasRed" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Priorities Tab */}
        <TabsContent value="priorities">
          <div className="flex justify-end mb-4">
            <Button 
              data-testid="add-priority-btn"
              onClick={() => openModal('priority')}
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {t('tracker.addPriority')}
            </Button>
          </div>
          <div className="space-y-3">
            {priorities.length === 0 ? (
              <div className="glass-card p-8 text-center">
                <p className="text-atlasTextSecondary">{t('tracker.noPriorities')}</p>
              </div>
            ) : (
              priorities.map((p) => (
                <div key={p.id} data-testid={`priority-item-${p.id}`} className="glass-card p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-2xl font-bold text-atlasCyan atlas-mono">#{p.rank}</span>
                    <div>
                      <h3 className="text-lg font-medium text-white">{p.title}</h3>
                      {p.description && <p className="text-atlasTextSecondary text-sm">{p.description}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openModal('priority', p)}>
                      <Edit2 className="w-4 h-4 text-atlasTextSecondary" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete('priority', p.id)}>
                      <Trash2 className="w-4 h-4 text-atlasRed" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="glass-card border-atlasBorder sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">
              {getModalTitle()}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-atlasTextSecondary">{t('tracker.titleField')}</Label>
              <Input
                data-testid="modal-title-input"
                value={formData.title || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="input-atlas mt-1"
                placeholder={t('tracker.titlePlaceholder')}
              />
            </div>

            <div>
              <Label className="text-atlasTextSecondary">{t('tracker.description')}</Label>
              <Textarea
                data-testid="modal-description-input"
                value={formData.description || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="input-atlas mt-1"
                placeholder={t('tracker.descriptionPlaceholder')}
              />
            </div>

            {modalType === 'decision' && (
              <>
                <div>
                  <Label className="text-atlasTextSecondary">{t('tracker.priorityLabel')}</Label>
                  <Select 
                    value={formData.priority || 'medium'} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                  >
                    <SelectTrigger data-testid="modal-priority-select" className="input-atlas mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">{t('tracker.priority.low')}</SelectItem>
                      <SelectItem value="medium">{t('tracker.priority.medium')}</SelectItem>
                      <SelectItem value="high">{t('tracker.priority.high')}</SelectItem>
                      <SelectItem value="critical">{t('tracker.priority.critical')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {editingItem && (
                  <div>
                    <Label className="text-atlasTextSecondary">{t('tracker.statusLabel')}</Label>
                    <Select 
                      value={formData.status || 'pending'} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                    >
                      <SelectTrigger data-testid="modal-status-select" className="input-atlas mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">{t('tracker.status.pending')}</SelectItem>
                        <SelectItem value="completed">{t('tracker.status.completed')}</SelectItem>
                        <SelectItem value="abandoned">{t('tracker.status.abandoned')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            {(modalType === 'decision' || modalType === 'commitment') && (
              <div>
                <Label className="text-atlasTextSecondary">{t('tracker.deadline')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      data-testid="modal-deadline-btn"
                      className="w-full input-atlas mt-1 justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.deadline ? format(new Date(formData.deadline), 'PPP') : t('tracker.pickDate')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-atlasCard border-atlasBorder">
                    <Calendar
                      mode="single"
                      selected={formData.deadline ? new Date(formData.deadline) : undefined}
                      onSelect={(date) => setFormData(prev => ({ ...prev, deadline: date }))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {modalType === 'priority' && (
              <div>
                <Label className="text-atlasTextSecondary">{t('tracker.rank')}</Label>
                <Input
                  type="number"
                  data-testid="modal-rank-input"
                  value={formData.rank || 1}
                  onChange={(e) => setFormData(prev => ({ ...prev, rank: parseInt(e.target.value) || 1 }))}
                  className="input-atlas mt-1"
                  min={1}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={closeModal} className="text-atlasTextSecondary">
              {t('common.cancel')}
            </Button>
            <Button 
              data-testid="modal-save-btn"
              onClick={handleSave} 
              disabled={saving || !formData.title}
              className="btn-primary"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Decisions;
